import React from 'react';
import './LoadingSpinner.css';

interface LoadingSpinnerProps {
  size?: 'small' | 'medium' | 'large';
  message?: string;
  fullScreen?: boolean;
  overlay?: boolean;
}

export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({ 
  size = 'medium', 
  message,
  fullScreen = false,
  overlay = false
}) => {
  const containerClass = `loading-spinner-container ${size} ${fullScreen ? 'fullscreen' : ''} ${overlay ? 'overlay' : ''}`;
  
  return (
    <div className={containerClass}>
      <div className="loading-content">
        <div className="spinner">
          <div className="spinner-ring"></div>
          <div className="spinner-ring"></div>
          <div className="spinner-ring"></div>
          <div className="spinner-ring"></div>
        </div>
        {message && <p className="loading-message">{message}</p>}
      </div>
    </div>
  );
};

interface SkeletonProps {
  width?: string;
  height?: string;
  borderRadius?: string;
  className?: string;
}

export const Skeleton: React.FC<SkeletonProps> = ({ 
  width = '100%', 
  height = '20px', 
  borderRadius = '4px',
  className = ''
}) => {
  return (
    <div 
      className={`skeleton ${className}`}
      style={{ width, height, borderRadius }}
    />
  );
};

export const ProjectCardSkeleton: React.FC = () => {
  return (
    <div className="project-card-skeleton">
      <Skeleton height="120px" borderRadius="8px 8px 0 0" />
      <div className="skeleton-content">
        <Skeleton height="24px" width="80%" />
        <Skeleton height="16px" width="100%" />
        <Skeleton height="16px" width="60%" />
        <div className="skeleton-footer">
          <Skeleton height="14px" width="40%" />
          <Skeleton height="14px" width="30%" />
        </div>
      </div>
    </div>
  );
};

interface SkeletonTextProps {
  lines?: number;
  className?: string;
}

/**
 * Renders a stack of skeleton lines to stand in for a paragraph of text.
 * The final line is shortened to mimic a natural ragged text edge.
 */
export const SkeletonText: React.FC<SkeletonTextProps> = ({ lines = 3, className = '' }) => {
  return (
    <div className={`skeleton-text ${className}`.trim()}>
      {[...Array(lines)].map((_, i) => (
        <Skeleton
          key={i}
          height="14px"
          width={i === lines - 1 ? '60%' : '100%'}
        />
      ))}
    </div>
  );
};

/**
 * Form-shaped skeleton — header bar plus a series of labelled field rows.
 * Used by the Settings page while user settings load.
 */
export const FormSkeleton: React.FC<{ fields?: number }> = ({ fields = 4 }) => {
  return (
    <div className="form-skeleton">
      <div className="form-skeleton-header">
        <Skeleton height="32px" width="200px" />
      </div>
      {[...Array(fields)].map((_, i) => (
        <div key={i} className="form-skeleton-field">
          <Skeleton height="14px" width="120px" />
          <Skeleton height="40px" width="100%" borderRadius="8px" />
        </div>
      ))}
      <div className="form-skeleton-actions">
        <Skeleton height="40px" width="140px" borderRadius="8px" />
      </div>
    </div>
  );
};

/**
 * Responsive grid of {@link ProjectCardSkeleton} placeholders. Used by the
 * Gallery and Achievements pages while their card data loads.
 */
export const CardGridSkeleton: React.FC<{ count?: number }> = ({ count = 8 }) => {
  return (
    <div className="card-grid-skeleton">
      {[...Array(count)].map((_, i) => (
        <ProjectCardSkeleton key={i} />
      ))}
    </div>
  );
};

/**
 * Vertical list skeleton — a stack of rows, each with an icon dot, a couple of
 * text lines and a trailing meta chip. Used by the Curriculum browser.
 */
export const ListSkeleton: React.FC<{ rows?: number }> = ({ rows = 5 }) => {
  return (
    <div className="list-skeleton">
      {[...Array(rows)].map((_, i) => (
        <div key={i} className="list-skeleton-row">
          <Skeleton width="32px" height="32px" borderRadius="50%" />
          <div className="list-skeleton-row-body">
            <Skeleton height="18px" width="40%" />
            <Skeleton height="14px" width="80%" />
          </div>
          <Skeleton width="70px" height="24px" borderRadius="12px" />
        </div>
      ))}
    </div>
  );
};

/**
 * Report-shaped skeleton — title block, a row of summary stat tiles and a
 * couple of section blocks. Used by the StudentReport page.
 */
export const ReportSkeleton: React.FC = () => {
  return (
    <div className="report-skeleton">
      <div className="report-skeleton-header">
        <Skeleton height="36px" width="320px" />
        <Skeleton height="16px" width="200px" />
      </div>
      <div className="report-skeleton-stats">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="report-skeleton-stat">
            <Skeleton height="40px" width="40px" borderRadius="8px" />
            <Skeleton height="14px" width="70%" />
            <Skeleton height="24px" width="40%" />
          </div>
        ))}
      </div>
      {[...Array(2)].map((_, i) => (
        <div key={i} className="report-skeleton-section">
          <Skeleton height="24px" width="180px" />
          <Skeleton height="80px" width="100%" borderRadius="8px" />
        </div>
      ))}
    </div>
  );
};

/**
 * Grid of template-card placeholders. Used by the NewProject page while the
 * template chooser warms up.
 */
export const TemplateGridSkeleton: React.FC<{ count?: number }> = ({ count = 6 }) => {
  return (
    <div className="template-grid-skeleton">
      {[...Array(count)].map((_, i) => (
        <div key={i} className="template-card-skeleton">
          <Skeleton width="48px" height="48px" borderRadius="50%" />
          <Skeleton height="18px" width="70%" />
          <Skeleton height="14px" width="90%" />
          <Skeleton height="14px" width="50%" />
        </div>
      ))}
    </div>
  );
};

export const DashboardSkeleton: React.FC = () => {
  return (
    <div className="dashboard-skeleton">
      <div className="dashboard-header-skeleton">
        <Skeleton height="40px" width="300px" />
        <div className="header-actions-skeleton">
          <Skeleton height="36px" width="120px" />
          <Skeleton height="36px" width="80px" />
        </div>
      </div>
      
      <div className="dashboard-content-skeleton">
        <div className="section-header-skeleton">
          <Skeleton height="32px" width="200px" />
          <Skeleton height="40px" width="150px" />
        </div>
        
        <div className="projects-grid-skeleton">
          {[...Array(6)].map((_, i) => (
            <ProjectCardSkeleton key={i} />
          ))}
        </div>
      </div>
    </div>
  );
};